import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Globe, Package, List } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9e7963b6-8d0a-418d-8246-af7a5b4f8387/artifacts/h6dgewwm_image.png';

export const Header = () => {
  const { t, toggleLanguage, language, requestCount, isAdmin, isBuyer, isBuyerApproved } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/products', label: t('nav.products') },
    { path: '/grading', label: t('nav.grading') },
    { path: '/refurbishment', label: t('nav.refurbishment') },
    { path: '/export', label: t('nav.export') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/faqs', label: t('nav.faqs') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || isOpen ? 'glass-heavy py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="LUXOR TEK" 
              className="h-10 md:h-12 object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#00D4FF]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-1 px-2 py-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>

            {/* Wholesale Register / Buyer Login CTA */}
            {isBuyer ? (
              <Link
                to="/buyer"
                data-testid="buyer-dashboard-link"
                className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#00D4FF] text-black hover:bg-[#00D4FF]/80 transition-colors"
              >
                {language === 'es' ? 'Mi Cuenta' : 'My Account'}
              </Link>
            ) : (
              <Link
                to="/buyer/login"
                data-testid="buyer-login-cta"
                className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#00D4FF] text-black hover:bg-[#00D4FF]/80 transition-colors"
              >
                {language === 'es' ? 'Iniciar Sesión' : 'Buyer Login'}
              </Link>
            )}

            {/* Bulk Order Builder — visible for approved buyers */}
            {isBuyerApproved && (
              <Link
                to="/bulk-order"
                data-testid="bulk-order-link"
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/bulk-order') ? 'text-[#10B981]' : 'text-[#10B981]/70 hover:text-[#10B981]'
                }`}
              >
                <Package className="w-4 h-4" />
                <span className="hidden lg:inline">{language === 'es' ? 'Pedidos' : 'Bulk Order'}</span>
              </Link>
            )}

            {/* Saved Lists — visible for approved buyers */}
            {isBuyerApproved && (
              <Link
                to="/buyer/saved-lists"
                data-testid="saved-lists-link"
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/buyer/saved-lists') ? 'text-[#00D4FF]' : 'text-white/50 hover:text-white'
                }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden lg:inline">{language === 'es' ? 'Listas' : 'Lists'}</span>
              </Link>
            )}

            {/* Cart / Request List — always links to /request */}
            <Link
              to="/request"
              data-testid="request-list-btn"
              className="relative flex items-center gap-2 px-3 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              {requestCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-[#00D4FF] text-black text-xs font-bold rounded-full">
                  {requestCount}
                </span>
              )}
            </Link>

            {/* Admin Link (if logged in) */}
            {isAdmin && (
              <Link
                to="/admin"
                data-testid="admin-dashboard-link"
                className="hidden md:flex px-3 py-2 text-sm font-medium text-[#00D4FF] hover:text-[#00A0FF] transition-colors"
              >
                {t('nav.admin')}
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              className="xl:hidden p-2 text-white"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <nav 
            data-testid="mobile-nav"
            className="xl:hidden mt-4 pb-4 border-t border-white/10 pt-4 bg-[#0A0A0A]/95 backdrop-blur-xl -mx-6 px-6 rounded-b-lg"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-[#00D4FF] bg-white/5'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              {/* Buyer link in mobile menu */}
              {isBuyer ? (
                <Link
                  to="/buyer"
                  className="px-4 py-3 text-sm font-medium text-[#00D4FF] hover:bg-white/5"
                >
                  {language === 'es' ? 'Mi Cuenta' : 'My Account'}
                </Link>
              ) : (
                <Link
                  to="/buyer/login"
                  className="px-4 py-3 text-sm font-medium text-[#00D4FF] hover:bg-white/5"
                >
                  {language === 'es' ? 'Iniciar Sesión' : 'Buyer Login'}
                </Link>
              )}
              {isBuyerApproved && (
                <Link
                  to="/bulk-order"
                  className={`px-4 py-3 text-sm font-medium flex items-center gap-2 ${
                    isActive('/bulk-order') ? 'text-[#10B981] bg-white/5' : 'text-[#10B981]/70 hover:text-[#10B981] hover:bg-white/5'
                  }`}
                >
                  <Package className="w-4 h-4" />
                  {language === 'es' ? 'Constructor de Pedidos' : 'Bulk Order Builder'}
                </Link>
              )}
              {isBuyerApproved && (
                <Link
                  to="/buyer/saved-lists"
                  className={`px-4 py-3 text-sm font-medium flex items-center gap-2 ${
                    isActive('/buyer/saved-lists') ? 'text-[#00D4FF] bg-white/5' : 'text-white/50 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <List className="w-4 h-4" />
                  {language === 'es' ? 'Listas Guardadas' : 'Saved Lists'}
                </Link>
              )}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-4 py-3 text-sm font-medium text-[#00D4FF] hover:bg-white/5"
                >
                  {t('nav.admin')}
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
